import { useState, useEffect, useRef, useMemo } from 'react';
import type { Project, MarketAnalysis, Positioning, Persona, BusinessModel } from '@/utils/pocketbase';
import { extractPersonaName } from '@/utils/markdown';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useTranslation } from 'react-i18next';
interface MvpPlanCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
  projects: Project[];
  marketAnalyses: MarketAnalysis[];
  positionings: Positioning[];
  personas: Persona[];
  businessModels: BusinessModel[];
  initialData?: FormData;
  isEditing?: boolean;
  isLoading?: boolean;
}

export interface FormData {
  project_id: string;
  market_analysis_id: string;
  persona_id: string;
  positioning_id: string;
  business_model_id: string;
  budget: string;
  comment: string;
}

export function MvpPlanCreationModal({
  isOpen,
  onClose,
  onSubmit,
  projects,
  marketAnalyses,
  positionings,
  personas,
  businessModels,
  initialData,
  isEditing = false,
  isLoading = false
}: MvpPlanCreationModalProps) {
  const { t } = useTranslation('mvp-plan');
  const [formData, setFormData] = useState<FormData>({
    project_id: '',
    market_analysis_id: '',
    persona_id: '',
    positioning_id: '',
    business_model_id: '',
    budget: '',
    comment: ''
  });

  const budgetRef = useRef<HTMLTextAreaElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    adjustHeight(budgetRef.current);
    adjustHeight(commentRef.current);
  }, [formData]);

  // Filter by selected project
  const filteredAnalyses = formData.project_id
    ? marketAnalyses.filter(a => a.project === formData.project_id)
    : marketAnalyses;

  const filteredPersonas = formData.project_id
    ? personas.filter(p => p.project === formData.project_id)
    : personas;

  const filteredPositionings = formData.project_id
    ? positionings.filter(p => p.project === formData.project_id)
    : positionings;

  const filteredBusinessModels = formData.project_id
    ? businessModels.filter(b => b.project === formData.project_id)
    : businessModels;

  const getPositioningTitle = (positioning: Positioning): string => {
    const project = projects.find(p => p.id === positioning.project);
    return t('modal.positioningItemTitle', { projectName: project?.name || t('defaultProjectName') });
  };

  // Count positionings per project for numbering (chronological: oldest = 1)
  const positioningNumbers = useMemo(() => {
    const projectCounts: Record<string, number> = {};
    const numbers: Record<string, { displayNumber: number; total: number }> = {};

    positionings.forEach(positioning => {
      const projectId = positioning.project || '';
      projectCounts[projectId] = (projectCounts[projectId] || 0) + 1;
    });

    const projectIndexes: Record<string, number> = {};
    [...positionings].reverse().forEach(positioning => {
      const projectId = positioning.project || '';
      projectIndexes[projectId] = (projectIndexes[projectId] || 0) + 1;
      numbers[positioning.id] = {
        displayNumber: projectIndexes[projectId] ?? 1,
        total: projectCounts[projectId] ?? 1
      };
    });

    return numbers;
  }, [positionings]);

  const getPositioningListTitle = (positioning: Positioning): string => {
    const numberInfo = positioningNumbers[positioning.id];
    const shouldShowNumber = numberInfo && numberInfo.total > 1;
    const displayNum = numberInfo?.displayNumber ?? 1;
    return `${getPositioningTitle(positioning)}${shouldShowNumber ? ` ${displayNum}` : ''}`;
  };

  const getBusinessModelTitle = (businessModel: BusinessModel): string => {
    const project = projects.find(p => p.id === businessModel.project);
    return t('modal.businessModelItemTitle', { projectName: project?.name || t('defaultProjectName') });
  };

  const businessModelNumbers = useMemo(() => {
    const projectCounts: Record<string, number> = {};
    const numbers: Record<string, { displayNumber: number; total: number }> = {};

    businessModels.forEach(bm => {
      const projectId = bm.project || '';
      projectCounts[projectId] = (projectCounts[projectId] || 0) + 1;
    });

    const projectIndexes: Record<string, number> = {};
    [...businessModels].reverse().forEach(bm => {
      const projectId = bm.project || '';
      projectIndexes[projectId] = (projectIndexes[projectId] || 0) + 1;
      numbers[bm.id] = {
        displayNumber: projectIndexes[projectId] ?? 1,
        total: projectCounts[projectId] ?? 1
      };
    });

    return numbers;
  }, [businessModels]);

  const getBusinessModelListTitle = (businessModel: BusinessModel): string => {
    const numberInfo = businessModelNumbers[businessModel.id];
    const shouldShowNumber = numberInfo && numberInfo.total > 1;
    const displayNum = numberInfo?.displayNumber ?? 1;
    return `${getBusinessModelTitle(businessModel)}${shouldShowNumber ? ` ${displayNum}` : ''}`;
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const isValid = formData.project_id
    && formData.market_analysis_id
    && formData.persona_id
    && formData.positioning_id
    && formData.budget.trim().length > 0;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-[16px]"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-[24px] p-[24px] md:p-[40px] max-w-[580px] w-full max-h-[90vh] overflow-y-auto">
        {isLoading ? (
          <GenerationProgress
            title={t('modal.progressTitle')}
            message={isEditing
              ? t('modal.progressMessageEdit')
              : t('modal.progressMessageCreate')}
          />
        ) : (
          <>
            {/* Header */}
            <div className="flex flex-col gap-[16px] mb-[32px]">
              <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d]">
                {isEditing ? t('modal.titleEdit') : t('modal.titleCreate')}
              </h2>
              <p className="font-segoe text-[16px] leading-[24px] text-[#565858]">
                {t('modal.subtitle')}
              </p>
            </div>

            {/* Form Fields */}
            <div className="flex flex-col gap-[24px] mb-[32px]">
              {/* Project Selection */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelProduct')}
                </label>
                <Select
                  value={formData.project_id}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    project_id: value,
                    market_analysis_id: '',
                    persona_id: '',
                    positioning_id: '',
                    business_model_id: ''
                  })}
                  disabled={isEditing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('modal.placeholderProduct')} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Market Analysis Selection */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelMarketAnalysis')}
                </label>
                <Select
                  value={formData.market_analysis_id}
                  onValueChange={(value) => setFormData({ ...formData, market_analysis_id: value })}
                  disabled={!formData.project_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('modal.placeholderProduct')} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredAnalyses.map((analysis, index) => {
                      const project = projects.find(p => p.id === analysis.project);
                      return (
                        <SelectItem key={analysis.id} value={analysis.id}>
                          {t('modal.analysisItemTitle', { projectName: project?.name || t('defaultProjectName') })}{filteredAnalyses.length > 1 ? ` (${index + 1})` : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Persona Selection */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelPersona')}
                </label>
                <Select
                  value={formData.persona_id}
                  onValueChange={(value) => setFormData({ ...formData, persona_id: value })}
                  disabled={!formData.project_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('modal.placeholderProduct')} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredPersonas.map(persona => (
                      <SelectItem key={persona.id} value={persona.id}>
                        {extractPersonaName(persona)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Positioning Selection */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelPositioning')}
                </label>
                <Select
                  value={formData.positioning_id}
                  onValueChange={(value) => setFormData({ ...formData, positioning_id: value })}
                  disabled={!formData.project_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('modal.placeholderProduct')} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredPositionings.map(positioning => (
                      <SelectItem key={positioning.id} value={positioning.id}>
                        {getPositioningListTitle(positioning)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Business Model Selection (optional) */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelBusinessModel')}
                </label>
                <Select
                  value={formData.business_model_id}
                  onValueChange={(value) => setFormData({ ...formData, business_model_id: value === '__none__' ? '' : value })}
                  disabled={!formData.project_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('modal.placeholderNone')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('modal.placeholderNone')}</SelectItem>
                    {filteredBusinessModels.map(bm => (
                      <SelectItem key={bm.id} value={bm.id}>
                        {getBusinessModelListTitle(bm)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Budget */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelBudget')}
                </label>
                <textarea
                  ref={budgetRef}
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={1}
                  placeholder={t('modal.placeholderBudget')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>

              {/* Comment */}
              <div className="flex flex-col gap-[4px]">
                <label className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                  {t('modal.labelComment')}
                </label>
                <textarea
                  ref={commentRef}
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  onInput={(e) => adjustHeight(e.currentTarget)}
                  rows={1}
                  placeholder={t('modal.placeholderComment')}
                  className="
                    w-full
                    bg-transparent border border-[#eeeeee] rounded-[16px] px-[16px] py-[12px]
                    font-segoe text-[16px] leading-[24px] text-[#0d0d0d]
                    placeholder:text-[#969696]
                    resize-none outline-none overflow-hidden
                  "
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-[24px]">
              <button
                onClick={onClose}
                className="
                  flex-1
                  bg-transparent
                  border border-[var(--grey-accent,#eeeeee)] border-solid
                  text-[color:var(--black-text,#0d0d0d)]
                  px-[24px] py-[12px]
                  rounded-[16px]
                  font-segoe font-semibold text-[16px] leading-[24px]
                  text-center
                  hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1]
                  transition-colors
                "
              >
                {t('modal.buttonClose')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isValid}
                className={`
                  flex-1
                  rounded-[16px] px-[24px] py-[12px]
                  font-segoe font-semibold text-[16px] leading-[24px]
                  text-center
                  transition-colors
                  ${isValid
                    ? 'bg-[#2197f1] text-white hover:bg-[#1a7bc7]'
                    : 'bg-[rgba(33,151,241,0.2)] text-[#2197f1] cursor-not-allowed opacity-60'
                  }
                `}
              >
                {isEditing ? t('modal.buttonUpdate') : t('modal.buttonCreate')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
